import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { HR } from 'foundations-components/transitional/components/HR';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { EcosystemReviewsFilters } from './EcosystemReviewsFilters';
import { EcosystemReviewsPaginator } from './EcosystemReviewsPaginator';
import { getDefaultRatingOptions, getDefaultSortOptions } from '../../utils/defaultFilters';
import ReviewFilters from '../../records/ReviewFilters';
import { useReviews } from '../../hooks/useReviews';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function EcosystemReviewsList({
  appContext = 'public',
  collapsedPageLimit = 2,
  expandable,
  expandButtonText,
  externalSearchTerm,
  offeringId,
  pageLimitOptions = [10],
  ratingOptions = getDefaultRatingOptions(),
  renderedEmptyState,
  ReviewRenderer,
  reviewRendererProps = {},
  reviewType,
  searchable = false,
  showPagination = true,
  sortOptions = getDefaultSortOptions()
}) {
  const reviewsListRef = useRef(null);
  const defaultLimit = pageLimitOptions[0];
  const [hasReviews, setHasReviews] = useState(false);
  const [reviewFilters, setReviewFilters] = useState(new ReviewFilters({
    entityId: offeringId,
    limit: expandable ? collapsedPageLimit : defaultLimit
  }));
  const {
    reviews,
    reviewsFetchStatus,
    reviewsTotal
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId,
    reviewType
  });
  const pageCount = Math.ceil(reviewsTotal / reviewFilters.limit);
  useEffect(() => {
    setReviewFilters(new ReviewFilters({
      entityId: offeringId,
      limit: expandable ? collapsedPageLimit : defaultLimit
    }));
  }, [defaultLimit, expandable, collapsedPageLimit, offeringId]);
  useEffect(() => {
    if (!hasReviews) {
      setHasReviews(reviewsTotal > 0);
    }
  }, [hasReviews, reviewsTotal]);
  useEffect(() => {
    if (externalSearchTerm != null) {
      var _window$matchMedia$ma, _window$matchMedia, _reviewsListRef$curre;
      setReviewFilters(prev => prev.updateFilters({
        search: externalSearchTerm,
        page: 1,
        limit: defaultLimit
      }));
      const prefersReducedMotion = (_window$matchMedia$ma = (_window$matchMedia = window.matchMedia('(prefers-reduced-motion: reduce)')) === null || _window$matchMedia === void 0 ? void 0 : _window$matchMedia.matches) !== null && _window$matchMedia$ma !== void 0 ? _window$matchMedia$ma : false;
      (_reviewsListRef$curre = reviewsListRef.current) === null || _reviewsListRef$curre === void 0 || _reviewsListRef$curre.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }
  }, [externalSearchTerm, defaultLimit]);
  const trackFilterChange = useCallback(updates => {
    if (reviewRendererProps !== null && reviewRendererProps !== void 0 && reviewRendererProps.onTrackInteraction) {
      let trackingAction = 'Filter reviews';
      switch (Object.keys(updates)[0]) {
        case 'limit':
          trackingAction = 'Expand reviews';
          break;
        case 'overallRating':
          trackingAction = `Filter reviews by rating: ${updates.overallRating}`;
          break;
        case 'page':
          trackingAction = 'Change review page';
          break;
        case 'search':
          trackingAction = 'Search reviews';
          break;
        case 'sort':
          trackingAction = `Sort reviews by ${updates.sort}`;
          break;
        default:
          trackingAction = 'Filter reviews';
          break;
      }
      reviewRendererProps === null || reviewRendererProps === void 0 || reviewRendererProps.onTrackInteraction({
        action: trackingAction,
        subscreen: 'Review list'
      });
    }
  }, [reviewRendererProps]);
  const handleUpdateFilters = useCallback(updates => {
    const formattedUpdates = {};
    Object.entries(updates).forEach(([key, value]) => formattedUpdates[key] = value === 'ALL' ? null : value);
    let newReviewFilters = reviewFilters.updateFilters(formattedUpdates);
    if (!formattedUpdates.page) {
      newReviewFilters = newReviewFilters.updateFilters({
        page: 1
      });
    }
    setReviewFilters(newReviewFilters);
    trackFilterChange(updates);
  }, [reviewFilters, trackFilterChange]);
  if (!hasReviews) {
    return /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: [reviewsFetchStatus],
      children: renderedEmptyState || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.list.empty"
      })
    });
  }
  return /*#__PURE__*/_jsxs(View, {
    "data-test-id": "reviews-list",
    ref: reviewsListRef,
    inlineSize: "100%",
    children: [/*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(EcosystemReviewsFilters, {
        showSearchBar: searchable,
        handleUpdateFilters: handleUpdateFilters,
        ratingOptions: ratingOptions,
        reviewFilters: reviewFilters,
        sortOptions: sortOptions
      })
    }), /*#__PURE__*/_jsxs(LoadingWrapper, {
      requestStatus: [reviewsFetchStatus],
      children: [reviewsTotal > 0 ? reviews.map(review => /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(ReviewRenderer, Object.assign({
          appContext: appContext,
          review: review,
          reviewType: reviewType,
          searchTerm: reviewFilters.search
        }, reviewRendererProps)), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        })]
      }, review.get('id'))) : /*#__PURE__*/_jsx(View, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.list.emptySearch"
        })
      }), showPagination && /*#__PURE__*/_jsx(View, {
        marginBlockStart: "800",
        children: /*#__PURE__*/_jsx(EcosystemReviewsPaginator, {
          collapsedPageLimit: collapsedPageLimit,
          expandable: expandable,
          expandButtonText: expandButtonText,
          onUpdateFilters: updates => {
            var _window$matchMedia$ma2, _window$matchMedia2, _reviewsListRef$curre2;
            handleUpdateFilters(updates);
            const prefersReducedMotion = (_window$matchMedia$ma2 = (_window$matchMedia2 = window.matchMedia('(prefers-reduced-motion: reduce)')) === null || _window$matchMedia2 === void 0 ? void 0 : _window$matchMedia2.matches) !== null && _window$matchMedia$ma2 !== void 0 ? _window$matchMedia$ma2 : false;
            (_reviewsListRef$curre2 = reviewsListRef.current) === null || _reviewsListRef$curre2 === void 0 || _reviewsListRef$curre2.scrollIntoView({
              behavior: prefersReducedMotion ? 'auto' : 'smooth'
            });
          },
          pageCount: pageCount,
          pageLimitOptions: pageLimitOptions,
          reviewFilters: reviewFilters
        })
      })]
    })]
  });
}